import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { User } from '../types/user';
import type { Patient } from '../types/patient';
import type { Visit } from '../types/visit';
import type { TimeRecord } from '../types/timeRecord';
import type { PhysicalAssessment } from '../types/assessment';
import type { NursingNote } from '../types/nursing';
import type { ShiftHandoff } from '../types/handoff';

interface AppState {
  // Auth
  user: User | null;
  setUser: (user: User | null) => void;
  
  // Patients
  patients: Patient[];
  addPatient: (patient: Patient) => void;
  updatePatient: (id: string, patient: Partial<Patient>) => void;
  
  // Visits
  visits: Visit[];
  todaysVisits: Visit[];
  currentVisit: Visit | null;
  setCurrentVisit: (visit: Visit | null) => void;
  addVisit: (visit: Visit) => void;
  updateVisitStatus: (id: string, status: Visit['status']) => void;
  
  // Time Records
  activeTimeRecord: TimeRecord | null;
  clockIn: (visitId: string, location?: { lat: number; lng: number }) => void;
  clockOut: (location?: { lat: number; lng: number }) => void;
  
  // Assessments
  assessments: PhysicalAssessment[];
  addAssessment: (assessment: PhysicalAssessment) => void;
  
  // Nursing Notes
  notes: NursingNote[];
  addNote: (note: NursingNote) => void;
  
  // Handoffs
  handoffs: ShiftHandoff[];
  addHandoff: (handoff: ShiftHandoff) => void;
  acknowledgeHandoff: (id: string) => void;
}

export const useStore = create<AppState>()(
  devtools(
    persist(
      (set, get) => ({
        // Auth
        user: null,
        setUser: (user) => set({ user }),
        
        // Patients
        patients: [],
        addPatient: (patient) => 
          set((state) => ({ patients: [...state.patients, patient] })),
        updatePatient: (id, updates) =>
          set((state) => ({
            patients: state.patients.map(p => 
              p.id === id ? { ...p, ...updates } : p
            )
          })),
        
        // Visits
        visits: [],
        todaysVisits: [],
        currentVisit: null,
        setCurrentVisit: (visit) => set({ currentVisit: visit }),
        addVisit: (visit) =>
          set((state) => ({ visits: [...state.visits, visit] })),
        updateVisitStatus: (id, status) =>
          set((state) => ({
            visits: state.visits.map(v =>
              v.id === id ? { ...v, status } : v
            )
          })),
        
        // Time Records
        activeTimeRecord: null,
        clockIn: (visitId, location) => {
          const user = get().user;
          if (!user) return;
          
          const timeRecord: TimeRecord = {
            id: crypto.randomUUID(),
            visit_id: visitId,
            nurse_id: user.id,
            clock_in_time: new Date().toISOString(),
            clock_in_location: location ? {
              lat: location.lat,
              lng: location.lng,
              address: 'Current Location'
            } : undefined,
            is_verified: false,
            created_at: new Date().toISOString()
          };
          
          set({ activeTimeRecord: timeRecord });
          
          // Update visit status
          get().updateVisitStatus(visitId, 'in_progress');
        },
        
        clockOut: (location) => {
          const activeRecord = get().activeTimeRecord;
          if (!activeRecord) return;
          
          const clockInTime = new Date(activeRecord.clock_in_time);
          const clockOutTime = new Date();
          const totalMinutes = Math.floor(
            (clockOutTime.getTime() - clockInTime.getTime()) / 60000
          );
          
          const updatedRecord: TimeRecord = {
            ...activeRecord,
            clock_out_time: clockOutTime.toISOString(),
            clock_out_location: location ? {
              lat: location.lat,
              lng: location.lng,
              address: 'Current Location'
            } : undefined,
            total_minutes: totalMinutes
          };
          
          // Update visit status
          get().updateVisitStatus(activeRecord.visit_id, 'completed');
          
          set({ activeTimeRecord: null });
          
          // In real app, save to database here
        },
        
        // Assessments
        assessments: [],
        addAssessment: (assessment) =>
          set((state) => ({ assessments: [...state.assessments, assessment] })),
        
        // Nursing Notes
        notes: [],
        addNote: (note) =>
          set((state) => ({ notes: [...state.notes, note] })),
        
        // Handoffs
        handoffs: [],
        addHandoff: (handoff) =>
          set((state) => ({ handoffs: [...state.handoffs, handoff] })),
        acknowledgeHandoff: (id) =>
          set((state) => ({
            handoffs: state.handoffs.map(h =>
              h.id === id 
                ? { ...h, is_acknowledged: true, acknowledged_at: new Date().toISOString() }
                : h
            )
          }))
      }),
      {
        name: 'premier-nursing-storage',
        partialize: (state) => ({ 
          user: state.user,
          activeTimeRecord: state.activeTimeRecord 
        })
      }
    )
  )
);

// Selectors
export const useCurrentUser = () => useStore((state) => state.user);
export const useTodaysVisits = () => {
  const visits = useStore((state) => state.visits);
  const today = new Date().toISOString().split('T')[0];
  return visits.filter(v => v.scheduled_date === today);
};
export const usePatientById = (id: string) => 
  useStore((state) => state.patients.find(p => p.id === id));
export const useActiveTimeRecord = () => useStore((state) => state.activeTimeRecord);
